/**
 * Meta Pixel Event Tracking Service
 * Pixel ID: 1292407476286121
 * 
 * Documentación: https://developers.facebook.com/docs/meta-pixel/reference
 */

// Verificar que fbq está disponible
const isFbqAvailable = () => {
  return typeof window !== 'undefined' && typeof window.fbq === 'function';
};

/**
 * Track PageView - Se llama automáticamente en el index.html
 * pero puede llamarse manualmente en navegación SPA
 */
export const trackPageView = () => {
  if (isFbqAvailable()) {
    window.fbq('track', 'PageView');
  }
};

/**
 * Track AddToCart - Cuando un usuario agrega un producto al carrito
 * @param {Object} params
 * @param {string} params.content_name - Nombre del producto
 * @param {string} params.content_ids - ID del producto
 * @param {string} params.content_type - Tipo (siempre 'product')
 * @param {number} params.value - Precio del producto
 * @param {string} params.currency - Moneda (COP)
 */
export const trackAddToCart = ({ content_name, content_ids, value, quantity = 1 }) => {
  if (isFbqAvailable()) {
    window.fbq('track', 'AddToCart', {
      content_name,
      content_ids: [content_ids],
      content_type: 'product',
      value,
      currency: 'COP',
      num_items: quantity,
    });
    console.log('[Meta Pixel] AddToCart:', content_name, value);
  }
};

/**
 * Track Purchase - Cuando se completa una compra/pedido
 * @param {Object} params
 * @param {number} params.value - Valor total de la compra
 * @param {Array} params.contents - Lista de productos [{id, quantity, item_price}]
 * @param {string} params.content_type - Tipo (siempre 'product')
 * @param {number} params.num_items - Cantidad total de items
 */
export const trackPurchase = ({ value, contents, num_items, order_id }) => {
  if (isFbqAvailable()) {
    window.fbq('track', 'Purchase', {
      value,
      currency: 'COP',
      content_type: 'product',
      contents,
      num_items,
      order_id,
    });
    console.log('[Meta Pixel] Purchase:', value, 'COP', num_items, 'items');
  }
};

/**
 * Track InitiateCheckout - Cuando el usuario inicia el proceso de checkout
 * @param {Object} params
 * @param {number} params.value - Valor del carrito
 * @param {number} params.num_items - Cantidad de items
 */
export const trackInitiateCheckout = ({ value, num_items, contents }) => {
  if (isFbqAvailable()) {
    window.fbq('track', 'InitiateCheckout', {
      value,
      currency: 'COP',
      content_type: 'product',
      contents,
      num_items,
    });
    console.log('[Meta Pixel] InitiateCheckout:', value, 'COP');
  }
};

/**
 * Track Lead - Cuando un vendedor potencial se registra
 * @param {Object} params
 * @param {string} params.content_name - Tipo de lead (ej: "Vendedor Digital", "Vendedor Carrito")
 */
export const trackLead = ({ content_name, content_category = 'Registro Vendedor' }) => {
  if (isFbqAvailable()) {
    window.fbq('track', 'Lead', {
      content_name,
      content_category,
    });
    console.log('[Meta Pixel] Lead:', content_name);
  }
};

/**
 * Track CompleteRegistration - Cuando se completa el registro de vendedor
 * @param {Object} params
 * @param {string} params.content_name - Tipo de vendedor
 * @param {string} params.status - Estado (pendiente, aprobado)
 */
export const trackCompleteRegistration = ({ content_name, status = 'pending' }) => {
  if (isFbqAvailable()) {
    window.fbq('track', 'CompleteRegistration', {
      content_name,
      status,
    });
    console.log('[Meta Pixel] CompleteRegistration:', content_name, status);
  }
};

/**
 * Track Contact - Cuando el usuario hace clic en WhatsApp u otro contacto
 * @param {Object} params
 * @param {string} params.content_name - Tipo de contacto (ej: "WhatsApp", "Llamada")
 */
export const trackContact = ({ content_name = 'WhatsApp', content_category = 'Contact' }) => {
  if (isFbqAvailable()) {
    window.fbq('track', 'Contact', {
      content_name,
      content_category,
    });
    console.log('[Meta Pixel] Contact:', content_name);
  }
};

/**
 * Track ViewContent - Cuando el usuario ve un producto o página importante
 * @param {Object} params
 * @param {string} params.content_name - Nombre del contenido
 * @param {string} params.content_ids - ID del contenido
 * @param {number} params.value - Valor (si aplica)
 */
export const trackViewContent = ({ content_name, content_ids, content_type = 'product', value }) => {
  if (isFbqAvailable()) {
    const data = {
      content_name,
      content_type,
    };
    if (content_ids) data.content_ids = [content_ids];
    if (value) {
      data.value = value;
      data.currency = 'COP';
    }
    window.fbq('track', 'ViewContent', data);
    console.log('[Meta Pixel] ViewContent:', content_name);
  }
};

/**
 * Track Search - Cuando el usuario busca productos
 * @param {Object} params
 * @param {string} params.search_string - Término de búsqueda
 */
export const trackSearch = ({ search_string }) => {
  if (isFbqAvailable()) {
    window.fbq('track', 'Search', {
      search_string,
    });
    console.log('[Meta Pixel] Search:', search_string);
  }
};

/**
 * Track Custom Event - Para eventos personalizados
 * @param {string} eventName - Nombre del evento
 * @param {Object} params - Parámetros adicionales
 */
export const trackCustomEvent = (eventName, params = {}) => {
  if (isFbqAvailable()) {
    window.fbq('trackCustom', eventName, params);
    console.log('[Meta Pixel] Custom:', eventName, params);
  }
};

// Eventos personalizados específicos para Baruj Shefa
export const trackWhatsAppClick = (source = 'landing') => {
  trackContact({ content_name: 'WhatsApp', content_category: source });
  trackCustomEvent('WhatsAppClick', { source });
};

export const trackCatalogView = (vendedor_nombre, vendedor_code) => {
  trackViewContent({ 
    content_name: `Catálogo: ${vendedor_nombre}`,
    content_ids: vendedor_code,
    content_type: 'product_group'
  });
  trackCustomEvent('CatalogView', { vendedor_nombre, vendedor_code });
};

export const trackVendorSignupStart = (tipo_vendedor) => {
  trackCustomEvent('VendorSignupStart', { tipo_vendedor });
};

export const trackVendorSignupComplete = (tipo_vendedor, zona) => {
  trackLead({ content_name: `Vendedor ${tipo_vendedor}` });
  trackCompleteRegistration({ content_name: tipo_vendedor, status: 'pending' });
  trackCustomEvent('VendorSignupComplete', { tipo_vendedor, zona });
};

export default {
  trackPageView,
  trackAddToCart,
  trackPurchase,
  trackInitiateCheckout,
  trackLead,
  trackCompleteRegistration,
  trackContact,
  trackViewContent,
  trackSearch,
  trackCustomEvent,
  trackWhatsAppClick,
  trackCatalogView,
  trackVendorSignupStart,
  trackVendorSignupComplete,
};
